import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Switch,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  HStack,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEnqueueSettlements, useSettlementGuard } from '@/hooks/useAdminData'
import { useToast } from '@/hooks/useToast'
import { SettlementCandidate } from '@/types'
import { InlineSkeleton } from '@/components/common/LoadingSkeleton'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import {
  findEntriesWithoutPrefix,
} from '@/components/common/triggerInputHelpers'
import { parseTextareaEntries } from './paymentManagementInputHelpers'
import { countSettlementExecutionResults } from './settlementTriggerResultCounts'

const DEFAULT_AUTO_LIMIT = 10

export function SettlementExecutionTrigger() {
  const { t } = useTranslation()
  const toast = useToast()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const subtitleColor = useColorModeValue('gray.600', 'gray.300')
  const resultsBg = useColorModeValue('gray.50', 'whiteAlpha.100')

  const [orderInput, setOrderInput] = useState('')
  const [forceExecution, setForceExecution] = useState(false)
  const [forceReason, setForceReason] = useState('')
  const [limit, setLimit] = useState(DEFAULT_AUTO_LIMIT)
  const [results, setResults] = useState<{
    total: number
    queued: number
    failed: number
    skipped: number
    items: SettlementCandidate[]
  } | null>(null)

  const { data: guardResponse, isLoading: guardLoading } = useSettlementGuard()
  const enqueueSettlements = useEnqueueSettlements()

  const guard = guardResponse?.guard

  const resetResults = useCallback(() => setResults(null), [])

  const handleSubmit = useCallback(async () => {
    const entries = parseTextareaEntries(orderInput)
    const invalidEntries = findEntriesWithoutPrefix(entries, 'ord_')

    if (invalidEntries.length > 0) {
      toast.warning({
        title: t('settlements.execute.invalidOrdersTitle'),
        description: invalidEntries.join(', '),
      })
      return
    }

    const orderIds = entries

    if (guard && !guard.queueConfigured) {
      resetResults()
      toast.warning({
        title: t('settlements.execute.guardMissingTitle'),
        description: t('settlements.execute.guardMissingDescription'),
      })
      return
    }

    if (guard && !guard.manualAllowed) {
      resetResults()
      toast.info({
        title: t('settlements.execute.guardManualDisabled'),
        description: t('settlements.execute.guardManualDescription', { mode: guard.mode }),
      })
      return
    }

    if (forceExecution && !forceReason.trim()) {
      toast.warning({ title: t('settlements.execute.fields.force.reasonRequired') })
      return
    }

    try {
      const payload: {
        orderIds?: string[]
        limit?: number
        force?: boolean
        forceReason?: string
      } = {}

      if (orderIds.length > 0) {
        payload.orderIds = orderIds
      } else {
        payload.limit = limit
      }

      if (forceExecution) {
        payload.force = true
        payload.forceReason = forceReason.trim()
      }

      const response = await enqueueSettlements.mutateAsync(payload)

      const { queued, failed, skipped } = countSettlementExecutionResults(response.results)
      const totalEvaluated = response.totalRequested ?? response.results.length

      if (queued > 0) {
        toast.success({ title: t('settlements.execute.success', { count: queued }) })
      }

      if (failed > 0) {
        toast.warning({
          title: t('settlements.execute.partialFailure', { count: failed }),
          description: response.results
            .filter(result => !result.queued)
            .map(result => `${result.orderId}: ${result.reason ?? 'unknown'}`)
            .join('\n'),
          duration: 8000,
        })
      }

      setResults({
        total: totalEvaluated,
        queued,
        failed,
        skipped,
        items: response.results,
      })

      setOrderInput('')
    } catch (error: any) {
      resetResults()
      toast.error({
        title: t('settlements.execute.errorTitle'),
        description: error?.message || t('settlements.execute.errorDescription'),
      })
    }
  }, [
    enqueueSettlements,
    forceExecution,
    forceReason,
    guard,
    orderInput,
    resetResults,
    t,
    toast,
    limit,
  ])

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6} shadow="sm" bg={cardBg}>
      <VStack align="stretch" spacing={5}>
        {guardLoading && (
          <HStack spacing={3} aria-live="polite">
            <InlineSkeleton isCircle width="14px" height="14px" />
            <Text fontSize="sm" color={subtitleColor}>
              {t('settlements.execute.guardLoading')}
            </Text>
          </HStack>
        )}

        {guard && !guard.queueConfigured && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('settlements.execute.guardMissingTitle')}</AlertTitle>
              <AlertDescription>{t('settlements.execute.guardMissingDescription')}</AlertDescription>
            </Box>
          </Alert>
        )}

        {guard && guard.queueConfigured && !guard.manualAllowed && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('settlements.execute.guardManualDisabled')}</AlertTitle>
              <AlertDescription>
                {t('settlements.execute.guardManualDescription', { mode: guard.mode })}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {guard && (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel>{t('settlements.execute.statMode')}</StatLabel>
              <StatNumber>{guard.mode.toUpperCase()}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t('settlements.execute.statQueue')}</StatLabel>
              <StatNumber>
                {guard.queueConfigured ? t('settlements.execute.enabled') : t('settlements.execute.notConfigured')}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t('settlements.execute.statManual')}</StatLabel>
              <StatNumber>
                {guard.manualAllowed ? t('settlements.execute.enabled') : t('settlements.execute.disabled')}
              </StatNumber>
            </Stat>
          </SimpleGrid>
        )}

        <Text fontSize="sm" color={subtitleColor}>
          {t('settlements.execute.subtitle')}
        </Text>

        <FormControl>
          <FormLabel>{t('settlements.execute.fields.orderIds')}</FormLabel>
          <Textarea
            value={orderInput}
            onChange={(event) => {
              setOrderInput(event.target.value)
              resetResults()
            }}
            placeholder={t('settlements.execute.fields.orderPlaceholder')}
            rows={6}
            fontFamily="mono"
          />
          <FormHelperText>{t('settlements.execute.fields.orderHelper')}</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>{t('settlements.execute.fields.limit')}</FormLabel>
          <DropdownSelect
            value={String(limit)}
            options={[5, 10, 20, 50, 100].map(value => ({
              value: String(value),
              label: String(value),
            }))}
            onChange={value => {
              setLimit(Number(value))
              resetResults()
            }}
            ariaLabel={t('settlements.execute.fields.limit')}
          />
          <FormHelperText>{t('settlements.execute.fields.limitHelper')}</FormHelperText>
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="force-settlement" mb="0">
            {t('settlements.execute.fields.force.label')}
          </FormLabel>
          <Switch
            id="force-settlement"
            isChecked={forceExecution}
            onChange={(event) => {
              setForceExecution(event.target.checked)
              if (!event.target.checked) {
                setForceReason('')
              }
              resetResults()
            }}
            colorScheme="orange"
          />
        </FormControl>
        {forceExecution && (
          <>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{t('settlements.execute.fields.force.warning')}</AlertDescription>
            </Alert>
            <FormControl isRequired>
              <FormLabel>{t('settlements.execute.fields.force.reasonLabel')}</FormLabel>
              <Textarea
                value={forceReason}
                onChange={(event) => setForceReason(event.target.value)}
                placeholder={t('settlements.execute.fields.force.reasonPlaceholder')}
                rows={3}
                maxLength={500}
              />
              <FormHelperText>
                {t('settlements.execute.fields.force.reasonHelper')}
              </FormHelperText>
            </FormControl>
          </>
        )}

        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={enqueueSettlements.isPending}
          loadingText={t('settlements.execute.button.loading')}
        >
          {t('settlements.execute.button.submit')}
        </Button>

        {results && (
          <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={resultsBg} p={4}>
            <Text fontWeight="semibold" mb={3}>
              {t('settlements.execute.results.title')}
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={3}>
              <Stat>
                <StatLabel>{t('settlements.execute.results.total')}</StatLabel>
                <StatNumber>{results.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t('settlements.execute.results.queued')}</StatLabel>
                <StatNumber color="green.400">{results.queued}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t('settlements.execute.results.failed')}</StatLabel>
                <StatNumber color="red.400">{results.failed}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t('settlements.execute.results.skipped')}</StatLabel>
                <StatNumber color="yellow.400">{results.skipped}</StatNumber>
              </Stat>
            </SimpleGrid>

            <VStack align="stretch" spacing={1}>
              {results.items.slice(0, 10).map(item => (
                <Text key={item.orderId} fontFamily="mono" fontSize="sm">
                  {item.orderId} · {item.queued ? t('settlements.execute.results.queuedStatus') : (item.reason ?? 'unknown')}
                </Text>
              ))}
              {results.items.length > 10 && (
                <Text fontSize="xs" color={subtitleColor}>
                  {t('settlements.execute.results.more', { count: results.items.length - 10 })}
                </Text>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
